import React, { useCallback, useState } from "react";
import { useDispatch } from "react-redux";
import { client } from "../../..";
import { WA_SEND_TEMPLATE } from "../../../Queries/WhatsApp";
import { upsertWaConversation, upsertWaMessage } from "../../../Slices/whatsappSlice";
import SidePanel from "./SidePanel";
import TemplateComposer from "./TemplateComposer";
import { sendErrorText, gqlErrorMessage } from "./waUtils";

/** Send an approved template into an existing conversation. */
const TemplatePickerPanel = ({ conversation, fillContext, compliance, onClose }) => {
  const dispatch = useDispatch();
  const [state, setState] = useState({ template: null, values: [], valid: false, blocked: false });
  const [sending, setSending] = useState(false);
  const [error, setError] = useState(null);
  const onChange = useCallback((s) => setState(s), []);

  const send = async () => {
    if (!state.valid || sending) return;
    setSending(true);
    setError(null);
    try {
      const { data } = await client.mutate({
        mutation: WA_SEND_TEMPLATE,
        variables: {
          conversationId: conversation.id,
          templateName: state.template.name,
          language: state.template.language,
          params: state.values.map((v) => String(v).trim()),
        },
      });
      const res = data.whatsappSendTemplate;
      if (!res.ok) {
        setError({ code: res.code, message: res.error });
        return;
      }
      if (res.message) dispatch(upsertWaMessage(res.message));
      if (res.conversation) dispatch(upsertWaConversation(res.conversation));
      onClose();
    } catch (err) {
      setError({ message: gqlErrorMessage(err) });
    } finally {
      setSending(false);
    }
  };

  return (
    <SidePanel
      wide
      title="Send a template"
      subtitle="Approved templates can be sent any time — they're the only option once the 24-hour window closes."
      onClose={onClose}
      footer={
        <>
          {error ? (
            <span className="wa-help" style={{ color: "var(--wa-danger)" }} role="alert">
              {sendErrorText(error)}
            </span>
          ) : (
            <span className="wa-help"><span className="wa-kbd">Ctrl</span>+<span className="wa-kbd">Enter</span> to send</span>
          )}
          <button type="button" className="wa-btn" onClick={onClose}>Cancel</button>
          <button type="button" className="wa-btn wa-btn--primary" disabled={!state.valid || sending} onClick={send}>
            {sending ? <i className="fa-solid fa-spinner wa-spin" /> : <i className="fa-solid fa-paper-plane" />} Send template
          </button>
        </>
      }
    >
      <div onKeyDown={(e) => { if (e.key === "Enter" && (e.ctrlKey || e.metaKey)) { e.preventDefault(); send(); } }}>
        <TemplateComposer fillContext={fillContext} compliance={compliance} onChange={onChange} />
      </div>
    </SidePanel>
  );
};

export default TemplatePickerPanel;
